import "./map.css";

import { apm } from "@elastic/apm-rum";
import {
  DispatchGeocoder,
  HEREGeocoder,
  Infra3dGeocoder,
  LinrefCoder,
  POISearch,
} from "@inovitas/geocoderjs";
import { IFillLocation } from "@inovitas/geocoderjs/dist/types/interfaces/IFillLocation";
import { IFullLocation } from "@inovitas/geocoderjs/dist/types/interfaces/IFullLocation";
import { IInfra3dgeocoderConfig } from "@inovitas/geocoderjs/dist/types/interfaces/Iinfra3dgeocoderConfig";
import { ILinrefcoderConfig } from "@inovitas/geocoderjs/dist/types/interfaces/ILinrefcoder";
import { IPOISearchConfig } from "@inovitas/geocoderjs/dist/types/interfaces/IPOISearchConfig";
import {
  CameraAltOutlined,
  MapOutlined,
  NavigateNext,
  Search,
} from "@mui/icons-material";
import {
  Box,
  IconButton,
  InputBase,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import ClientConfig from "shared/config";
import UseAuth from "shared/common/hooks/UseAuth";
import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { manageKeyboardEvents } from "shared/common/utils/keyboardEvents/manageKeyboardEvents";
import geocoderConfig from "./geocoderconfig";
import MapContent from "./MapContent";

export default function Map() {
  const { userData } = UseAuth();

  const viewer = defaultStore((state) => state.viewer);
  const darkTheme = defaultStore((state) => state.darkTheme);
  const mapExpand = defaultStore((state) => state.mapExpand);
  const changeMapExpand = defaultStore((state) => state.changeMapExpand);
  const showStreetMap = defaultStore((state) => state.showStreetMap);
  const changeLoading = defaultStore((state) => state.changeLoading);
  const changeAlertMessage = defaultStore((state) => state.changeAlertMessage);
  const projectConfig = defaultStore((state) => state.projectConfig);
  const showMapWindow = defaultStore((state) => state.showMapWindow);
  const clientConfig = defaultStore((state) => state.clientConfig);

  const [searchInput, setSearchInput] = useState<string>("");
  const [zeroResults, setZeroResults] = useState<boolean>(false);
  const [suggestionList, setSuggestionList] = useState<IFillLocation[]>([]);
  const [myGeocoder, setMyGeocoder] = useState<DispatchGeocoder | undefined>();

  const { t } = useTranslation();

  //parse config
  const _parseConfig2V2 = function (config: any) {
    // What does this name mean? 1
    if (config.geocoders) return config;
    return {
      name: "search",
      geocoders: [
        {
          name: "infra3dgeocoder",
          datasets: config.geocoder,
          examples: config.examples,
        },
      ],
    };
  };

  //set mygeocoder on default state first time when render this component
  useEffect(() => {
    const html_input_element = document.getElementById(
      "infra3d-searchField"
    ) as HTMLInputElement;
    if (html_input_element !== null) {
      setMyGeocoder(new DispatchGeocoder(html_input_element));
    }
  }, []);

  // initialize geocoder
  useEffect(() => {
    const html_input_element = document.getElementById(
      "infra3d-searchField"
    ) as HTMLInputElement;
    if (html_input_element && myGeocoder) {
      let infra3dgeocoder;
      let here_geocoder: HEREGeocoder;
      let here_geocoder_bbox: string;
      const search_configuration = _parseConfig2V2(geocoderConfig);
      let examples: string[] = [];

      // linrefConfig has to come from the pilegate backend
      if (
        clientConfig?.map.geo_coders &&
        userData?.scope.includes("addon/linref")
      ) {
        const linrefConfig = clientConfig?.map.geo_coders.find(
          (geo_coder: any) => geo_coder.name === "linrefcode"
        );

        if (linrefConfig) {
          const linrefcoder = new LinrefCoder(html_input_element, {
            host: ClientConfig.linrefcodeURL,
            endpoint: "/kmpoints",
            datasets: linrefConfig.datasets,
            name: "linrefcode",
            apiKey: process.env.API_KEY,
          } as ILinrefcoderConfig);
          myGeocoder.registerGeocoder(linrefcoder);
        }
      }

      for (const key in search_configuration.geocoders) {
        const geocoder_config = search_configuration.geocoders[key];
        examples = examples.concat(geocoder_config.examples || []);
        switch (geocoder_config.name) {
          case "infra3dgeocoder": {
            infra3dgeocoder = new Infra3dGeocoder(html_input_element, {
              host: ClientConfig.geocoderURL,
              datasets: geocoder_config.datasets,
            } as IInfra3dgeocoderConfig);
            myGeocoder.registerGeocoder(infra3dgeocoder);
            break;
          }
          case "geofeatureserver": {
            const poisearch = new POISearch(html_input_element, {
              host: ClientConfig.geofeatureserverURL,
              dataset: geocoder_config.dataset,
              layer: geocoder_config.layer,
              queryproperty: geocoder_config.queryproperty,
              filters: geocoder_config.filters,
              // label: geocoder_config.label,
            } as IPOISearchConfig);
            myGeocoder.registerGeocoder(poisearch);
            break;
          }
          case "here": {
            here_geocoder = new HEREGeocoder(
              html_input_element,
              geocoder_config
            );
            myGeocoder.registerGeocoder(here_geocoder);
            if (projectConfig?.scope?.boundaries) {
              const coords =
                projectConfig.scope.boundaries.geometry.coordinates[0];
              // Used to check if inside this polygon due to not supporting bounding polygon by here.
              // The request does the bbox, geocoderjs checks the polygon.
              here_geocoder.boundingpolygon = coords;

              const lons = coords.map((latlon: number[]) => {
                return latlon[0];
              });
              const lats = coords.map((latlon: number[]) => {
                return latlon[1];
              });
              const bbox_array = [
                Math.min.apply(Math, lons),
                Math.min.apply(Math, lats),
                Math.max.apply(Math, lons),
                Math.max.apply(Math, lats),
              ];
              here_geocoder_bbox = "bbox:" + bbox_array.join(",");
              here_geocoder.configure({
                bbox: here_geocoder_bbox,
              });
            } else {
              let epsgCode = clientConfig?.epsg;

              if (!epsgCode) {
                const geoframe = viewer?.getScene().getCurrentGeoframe();
                if (!geoframe) break;
                epsgCode = geoframe.srid;
                if (!epsgCode) {
                  here_geocoder.configure({
                    bbox: "",
                  });
                  break;
                }
              }

              // If no project boundary, try to limit to the area of the project by EPSG code.
              fetch("https://epsg.io/" + epsgCode + ".json")
                .then((response) => response.json())
                .then((data) => {
                  const bbox = data?.bbox;
                  if (
                    bbox &&
                    bbox.west_longitude &&
                    bbox.south_latitude &&
                    bbox.east_longitude &&
                    bbox.north_latitude
                  ) {
                    here_geocoder.configure({
                      bbox: `bbox:${bbox.west_longitude},${bbox.south_latitude},${bbox.east_longitude},${bbox.north_latitude}`,
                    });
                  }
                })
                .catch(() => {
                  // If the fetch fails, just do not use a bbox.
                  here_geocoder.configure({
                    bbox: "",
                  });
                });
            }
            break;
          }
        }
      }
      myGeocoder.startAutocomplete();
      myGeocoder.on("autocomplete", (positions: IFillLocation[]) => {
        setSuggestionList(positions);
        if (positions.length === 0) {
          setZeroResults(true);
        } else {
          setZeroResults(false);
        }
      });
    }
  }, [viewer?.getScene()?.getCurrentGeoframe()?.srid]);

  //change state-s for sugeestion list when search input is empty
  useEffect(() => {
    if (searchInput.length === 0) {
      setSuggestionList([]);
      setZeroResults(false);
    }
  }, [searchInput, zeroResults]);

  //move to position that user have clicked on map
  const handlePosition = (locationId: string) => {
    if (viewer === undefined) return;
    let _location: IFullLocation | undefined;
    changeLoading(true);
    if (myGeocoder) {
      myGeocoder.select(locationId).then((location: IFullLocation) => {
        _location = location;
        monitor(apm, "click-on-map-geocode | Map", undefined, { count: 1 });

        if (location.position === undefined) {
          changeAlertMessage({
            message: t("alert_error.missing_image"),
            type: "error",
          });
          changeLoading(false);
          return;
        }

        viewer
          .movePositionLookAtPosition(
            {
              easting: location.position.lon,
              northing: location.position.lat,
            },
            50,
            location.source === "metadb"
              ? viewer.getScene().getCurrentGeoframe().srid
              : 4326
          )
          .then(() => {
            changeLoading(false);
          })
          .catch(() => {
            changeAlertMessage({
              message: t("alert_error.missing_image"),
              type: "error",
            });
            changeLoading(false);
          });
      });
    }
  };

  return (
    <Box
      sx={{
        height: mapExpand ? "100%" : "64px",
        maxHeight: "488px",
        display: showMapWindow ? "block" : "none",
        pointerEvents: "auto",
        transition: "padding 0.3s , height 0.3s , background-color 0.3s",
        borderRadius: "10px 0px 0px 10px",
        overflow: "hidden",
        backgroundColor: mapExpand ? "whiteBackground.main" : "darkblue.main",
        backdropFilter: "blur(10px)",
      }}
    >
      <Box
        sx={{
          padding: 1.5,
          backgroundColor: "darkblue.main",
          borderRadius: "10px 0px 0px 0px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: mapExpand ? 0.5 : 0,
            alignItems: "center",
            transition: "border-bottom 0.3s",
          }}
        >
          <IconButton
            classes={{
              root: mapExpand ? "infra3d-iconButtonRipple" : "",
            }}
            sx={{
              width: mapExpand ? "40px" : "0px",
              height: "40px",
              opacity: mapExpand ? 1 : 0,
              padding: "0px",
              overflow: "hidden",
              transition: "opacity width 0.3s",
              color: "white",
              "@media (hover: hover)": {
                ":hover": { color: "secondary.main" },
              },
            }}
            onClick={() =>
              monitor(apm, "close-map-window | Map", () => {
                changeMapExpand(false);
              })
            }
            disableRipple={!mapExpand}
          >
            <NavigateNext />
          </IconButton>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: mapExpand ? "primary.light" : "transparent",
              borderRadius: "5px",
            }}
          >
            <Tooltip
              title={
                !mapExpand
                  ? showStreetMap
                    ? t("tooltip.view")
                    : t("tooltip.map")
                  : ""
              }
              placement="left"
              enterDelay={400}
              classes={{
                tooltip: "infra3d-buttonDescription",
                popper: "infra3d-buttonDescriptionPopperRight",
              }}
            >
              <IconButton
                classes={{
                  root: mapExpand ? "" : "infra3d-iconButtonRipple",
                }}
                disabled={mapExpand}
                sx={{
                  color: "white",
                  "@media (hover: hover)": {
                    ":hover": { color: "secondary.main" },
                  },
                  ":disabled": { color: "white" },
                }}
                onClick={() =>
                  monitor(apm, "open-map-window | Map", () => {
                    changeMapExpand(true);
                  })
                }
              >
                {mapExpand ? (
                  <Search />
                ) : showStreetMap ? (
                  <CameraAltOutlined />
                ) : (
                  <MapOutlined />
                )}
              </IconButton>
            </Tooltip>
            <InputBase
              id="infra3d-searchField"
              sx={{
                color: "white",
                width: mapExpand ? "216px" : "0px",
                overflow: "hidden",
                transition: " width 0.3s",
              }}
              placeholder={t("tooltip.search_placeholder") || undefined}
              onChange={(event) => {
                setSearchInput(event.target.value);
              }}
              value={searchInput}
              onFocus={() => manageKeyboardEvents(viewer, true)}
              onBlur={() => manageKeyboardEvents(viewer, false)}
            />
            {searchInput !== "" && (
              <Box
                sx={{
                  position: "absolute",
                  width: mapExpand ? "256px" : "0px",
                  top: "64px",
                  height: "auto",
                  maxHeight: "calc(100% - 64px)",
                  backgroundColor: darkTheme ? "darkblue.main" : "white",
                  zIndex: 1,
                  overflow: "auto",
                  borderRadius: "0px 0px 5px 5px",
                }}
              >
                {zeroResults && suggestionList.length === 0 && (
                  <Typography
                    className="infra3d-searchSuggestionText"
                    sx={{
                      paddingX: 2,
                      paddingY: 1.5,
                      color: darkTheme ? "white" : "darkblue.main",
                    }}
                  >
                    {t("searchfield.no_items_found")}
                  </Typography>
                )}
                {!zeroResults && suggestionList.length === 0 && (
                  <Box
                    sx={{
                      paddingX: 2,
                      paddingY: 1.5,
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: "12px",
                        height: "12px",
                        borderTop: 2,
                        borderColor: darkTheme ? "white" : "darkblue.main",
                        borderRadius: "50%",
                        animation: "loadingSpin 1s linear infinite",
                      }}
                    ></Box>
                    <Typography
                      sx={{ color: darkTheme ? "white" : "darkblue.main" }}
                      className="infra3d-searchSuggestionText"
                    >
                      {t("general.loading")}
                    </Typography>
                  </Box>
                )}
                {suggestionList.length > 0 && (
                  <List disablePadding>
                    {suggestionList.map((item) => (
                      <ListItem key={item.locationId} disablePadding>
                        <ListItemButton
                          onClick={() => {
                            handlePosition(item.locationId);
                            setSearchInput("");
                          }}
                        >
                          <ListItemText
                            sx={{
                              color: darkTheme ? "white" : "darkblue.main",
                            }}
                            classes={{
                              primary: "infra3d-searchSuggestionText",
                            }}
                            primary={item.geocoder + ": " + item.label}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          height: "calc(100% - 80px)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: mapExpand ? 1 : 0,
          padding: mapExpand ? 1 : 0,
          transition: "padding 0.3s , gap 0.3s",
        }}
      >
        <MapContent />
      </Box>
    </Box>
  );
}
